import React, { useState, useEffect } from "react";

import { Label } from "app/components/ui/label";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";
import { toast } from "app/components/ui/use-toast";
import { RadioGroup, RadioGroupItem1 } from "app/components/ui/radio-group";
import { useLoginSlice } from "../slice";

interface ConfirmNumberProps {
  numbers: string[];
  onSelect: (number: string, otp?: string) => void;
  isLoading?: boolean;
  error?: string;
}

export const ConfirmNumber: React.FC<ConfirmNumberProps> = ({
  numbers,
  onSelect,
  isLoading = false,
  error,
}) => {
  const [selected, setSelected] = useState("");
  const { useSendCodeToPhoneMutation } = useLoginSlice();
  const [
    sendCodeToPhone,
    { isLoading: isSendingCode, isError, isSuccess, data, error: apiError },
  ] = useSendCodeToPhoneMutation();

  const handleContinue = async () => {
    if (selected) {
      try {
        await sendCodeToPhone({ phone: selected });
      } catch (error) {
        console.error("Error sending code:", error);
      }
    }
  };

  const maskNumber = (number: string) => {
    // Handle different phone number formats
    const cleanNumber = number.replace(/\D/g, "");
    if (cleanNumber.length === 10) {
      return cleanNumber.replace(/(\d{3})(\d{3})(\d{4})/, "(***) ***-$3");
    } else if (cleanNumber.length === 11 && cleanNumber.startsWith("1")) {
      return cleanNumber.replace(
        /(\d{1})(\d{3})(\d{3})(\d{4})/,
        "+1 (***) ***-$4"
      );
    } else {
      // Fallback for other formats
      return number.replace(/(\d{3})(\d{3})(\d{4})/, "(***) ***-$3");
    }
  };

  useEffect(() => {
    if (error) {
      toast({
        title: "Oops!",
        description: error,
        variant: "error",
      });
    }
  }, [error]);

  useEffect(() => {
    if (isSuccess && data) {
      toast({
        title: "Success!",
        description: data.message || "Verification code sent successfully",
        variant: "success",
      });
      onSelect(selected, data.data?.twoFaCode);
    }
  }, [isSuccess, data, selected, onSelect]);

  useEffect(() => {
    if (isError && apiError) {
      toast({
        title: "Oops!",
        description:
          typeof apiError === "string"
            ? apiError
            : "Failed to send verification code",
        variant: "error",
      });
    }
  }, [isError, apiError]);

  // If no phone numbers are available, show a message
  if (numbers.length === 0) {
    return (
      <div className="grid place-items-center w-full h-[calc(100dvh-8rem)] sm:h-[calc(100vh-9.625rem)] lg:h-[calc(100vh-11.375rem)] max-h-[60rem]">
        <div className="w-[90%] max-w-[31.25rem] bg-white p-4 sm:p-5 md:p-6 lg:p-[1.625rem] border border-[#E5E7EB] rounded-xl md:rounded-2xl">
          <div className="space-y-0.5 sm:space-y-1">
            <h4 className="text-xl sm:text-[1.375rem] font-bold leading-5 sm:leading-6 text-black md:leading-7 md:text-2xl font-mobile">
              No Phone Numbers Available
            </h4>
            <p className="text-xs sm:text-[0.825rem] font-medium leading-4 text-black opacity-50 sm:leading-5 md:text-sm">
              Please contact support to add a phone number to your account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid place-items-center w-full h-[calc(100dvh-8rem)] sm:h-[calc(100vh-9.625rem)] lg:h-[calc(100vh-11.375rem)] max-h-[60rem]">
      <form className="w-[90%] max-w-[31.25rem] bg-white p-4 sm:p-5 md:p-6 lg:p-[1.625rem] border border-[#E5E7EB] rounded-xl md:rounded-2xl">
        <div className="mb-3 space-y-0.5 sm:space-y-1 sm:mb-4 lg:mb-5">
          <h4 className="text-xl sm:text-[1.375rem] font-bold leading-5 sm:leading-6 text-black md:leading-7 md:text-2xl font-mobile">
            Let's confirm it's you
          </h4>
          <p className="text-xs sm:text-[0.825rem] font-medium leading-4 text-black opacity-50 sm:leading-5 md:text-sm">
            How should we verify your account?
          </p>
        </div>
        <RadioGroup
          value={selected}
          onValueChange={setSelected}
          className="gap-2 sm:gap-3 lg:gap-[0.875rem] mb-3 sm:mb-4 lg:mb-5"
        >
          {numbers.map((num) => (
            <div
              key={num}
              className="flex items-center space-x-1 sm:space-x-1.5 lg:space-x-2"
            >
              <RadioGroupItem1 value={num} id={num} />
              <Label
                htmlFor={num}
                className="text-black text-[0.775rem] sm:text-[0.825rem] lg:text-sm font-semibold leading-4 sm:leading-5 mb-0 cursor-pointer"
              >
                Text a code to {maskNumber(num)}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <Button
          type="submit"
          variant="primary"
          onClick={handleContinue}
          disabled={isLoading || isSendingCode || !selected}
        >
          {isLoading || isSendingCode ? (
            <div className="flex items-center gap-0.5 sm:gap-1">
              <Icons.Spinner className="w-4 h-4 animate-spin md:w-5 md:h-5" />
              <span>Sending...</span>
            </div>
          ) : (
            "Continue"
          )}
        </Button>
        <p className="text-black text-[0.715rem] sm:text-xs font-normal leading-4 mt-2.5 sm:mt-3 lg:mt-4">
          <span className="font-semibold">Note: </span>
          Vle Mobile will never call to ask for your password or verification
          code
        </p>
      </form>
    </div>
  );
};

export const isMobileDevice = () => {
  // Check if it's a real mobile device by looking for touch capability and screen size
  const hasTouchScreen =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileUA =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent
    );

  // Only return true if it's actually a mobile device with touch capability and small screen
  return hasTouchScreen && isSmallScreen && isMobileUA;
};
